import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'query-string';
import { notification } from 'antd';
import { IResponseCode } from 'interfaces/base';
import clientStore from 'store/client-store';
import i18n from 'i18n';
import socketStore from 'store/socket';
import * as Sentry from '@sentry/browser';
import { sendAPISentry } from 'sentry';

const BASE_URL = '/vkyc/client';
const TEST_BASE_URL = '/vkyc/test';
const COMMON_BASE_URL = '/vkyc';

const errorInterceptor = (err: AxiosError) => {
  const { response } = err;
  if (response) {
    const { status, data } = response;
    if (status === 403) {
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    } else {
      sendAPISentry(response);
    }
    notification.error({
      message: `${i18n.t('common.error')}`,
      description: `${status}: ${data.message}`,
      duration: 8,
    });
  }
  throw err;
};

const ajax = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  paramsSerializer: (params) => {
    return qs.stringify(params);
  },
});

const responseInterceptor = (res: AxiosResponse) => {
  if (res.data.code === IResponseCode.SUCCESS) {
    return res;
  } else {
    if (res.data.code === IResponseCode.EXPIRED_TOKEN) {
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    }
    const err: AxiosError = {
      isAxiosError: false,
      request: res.request,
      name: res.data.code,
      config: res.config,
      code: res.data.code,
      message: res.data.message,
      response: res,
      toJSON: () => Object,
    };
    throw err;
  }
};

const ajaxRequestInterceptor = (config: AxiosRequestConfig) => {
  config.headers = {
    'X-Transaction-Id': clientStore.getSession()?.transactionId,
    'X-Access-Token': clientStore.getAccessToken() || '',
  };
  return config;
};

const testAjax = axios.create({
  baseURL: TEST_BASE_URL,
  timeout: 60000,
  paramsSerializer: (params) => {
    return qs.stringify(params);
  },
});

ajax.interceptors.response.use(responseInterceptor, errorInterceptor);
ajax.interceptors.request.use(ajaxRequestInterceptor);

testAjax.interceptors.response.use(responseInterceptor, errorInterceptor);
testAjax.interceptors.request.use(ajaxRequestInterceptor);

export { ajax, testAjax };
